import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Lock, ChevronLeft, Check, CheckCheck, ArrowRight, Heart, Briefcase, DollarSign, Gem, Activity, Brain, Star, Palette, Calendar, Shield, Zap, Sparkles, Target, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { API_BASE as API } from "@/config";
import { CountdownTimer, SocialProofToast } from "@/components/CROWidgets";
import { GA } from "@/lib/analytics";
import confetti from "canvas-confetti";
import axios from "axios";
import { downloadReportPdf } from "@/lib/reportPdf";
import { getActiveReferral } from "@/lib/referral";

const analysisSteps = [
  { en: "Analyzing Name Energy...", hi: "नाम की एनर्जी देख रहे हैं..." },
  { en: "Calculating Personality...", hi: "व्यक्तित्व कैलकुलेट कर रहे हैं..." },
  { en: "Love Prediction...", hi: "प्यार की भविष्यवाणी..." },
  { en: "Money & Career Analysis...", hi: "पैसे और करियर विश्लेषण..." },
  { en: "Future Partner Match...", hi: "भावी साथी मैच..." },
  { en: "Hidden Talents Discovery...", hi: "छिपी प्रतिभा खोज..." },
  { en: "Generating Report...", hi: "रिपोर्ट बना रहे हैं..." },
];

const reportCards = [
  { emoji: "❤️", en: "Love Score", hi: "प्यार का स्कोर", score: "87%", color: "#FF4D6A" },
  { emoji: "💰", en: "Wealth Score", hi: "धन स्कोर", score: "72%", color: "#FFC107" },
  { emoji: "💼", en: "Career Match", hi: "करियर", score: "91%", color: "#4CAF50" },
  { emoji: "🧠", en: "Personality", hi: "व्यक्तित्व", score: "A+", color: "#2196F3" },
  { emoji: "💍", en: "Future Partner", hi: "भावी साथी", score: "High", color: "#E91E63" },
  { emoji: "🌟", en: "Hidden Talent", hi: "छिपी प्रतिभा", score: "3 Found", color: "#9C27B0" },
];

function TypingIndicator() {
  return (
    <div className="flex items-center gap-1 px-4 py-3">
      <div className="w-2 h-2 rounded-full bg-gray-400 typing-dot" />
      <div className="w-2 h-2 rounded-full bg-gray-400 typing-dot" />
      <div className="w-2 h-2 rounded-full bg-gray-400 typing-dot" />
    </div>
  );
}

function ChatBubble({ message, isUser, isNew }) {
  return (
    <motion.div initial={isNew ? { opacity: 0, y: 15, scale: 0.95 } : false} animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.3, ease: [.22,1,.36,1] }}
      className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3`}>
      {!isUser && (
        <img src="/logo.jpg" alt="" className="w-8 h-8 rounded-full object-cover mr-2 mt-1 flex-shrink-0 shadow-sm" />
      )}
      <div className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${isUser ? "chat-bubble-user rounded-br-md" : "chat-bubble-ai rounded-bl-md"}`}>
        {message}
        {!isUser && (
          <div className="flex items-center justify-end gap-1 mt-1">
            <span className="text-[10px] text-gray-400">{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            <CheckCheck className="w-3 h-3 text-blue-400" />
          </div>
        )}
      </div>
    </motion.div>
  );
}

function ScoreBar({ label, score, color, icon, delay = 0 }) {
  const [animated, setAnimated] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setAnimated(true), delay);
    return () => clearTimeout(t);
  }, [delay]);
  return (
    <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: delay / 1000, duration: 0.5 }}
      className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + '15' }}>
            {icon}
          </div>
          <span className="text-sm font-semibold text-gray-800">{label}</span>
        </div>
        <span className="text-lg font-bold" style={{ color, fontFamily: 'Space Grotesk' }}>{score}%</span>
      </div>
      <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
        <motion.div className="h-full rounded-full" style={{ backgroundColor: color }}
          initial={{ width: 0 }} animate={{ width: animated ? `${score}%` : 0 }}
          transition={{ duration: 1.2, ease: "easeOut", delay: delay / 1000 }} />
      </div>
    </motion.div>
  );
}

export default function CuriosityPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const testSource = searchParams.get("test") || "direct";
  const resumeSessionId = searchParams.get("session");
  const { language, t } = useLanguage();
  const [phase, setPhase] = useState("chat"); // chat | analyzing | report | unlocked
  const [chatMessages, setChatMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [chatStep, setChatStep] = useState(0); // 0=greeting, 1=name, 2=dob, 3=gender, 4=question, 5=done
  const [typing, setTyping] = useState(false);
  const [formData, setFormData] = useState({ name: "", birth_date: "", gender: "", question: "" });
  const [sessionId, setSessionId] = useState(null);
  const [analysisStep, setAnalysisStep] = useState(0);
  const [payLoading, setPayLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [chatEnded, setChatEnded] = useState(false);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  useEffect(() => { scrollToBottom(); }, [chatMessages, typing]);

  const addAIMessage = (text, delay = 800) => {
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setChatMessages(prev => [...prev, { text, isUser: false, isNew: true }]);
    }, delay);
  };

  const greetingSent = useRef(false);
  useEffect(() => {
    if (greetingSent.current || resumeSessionId) return;
    greetingSent.current = true;
    GA.chatStarted();
    setTimeout(() => {
      addAIMessage("👋 Vishu Astro mein aapka swagat hai!", 600);
      setTimeout(() => {
        addAIMessage("Main aapki personalized astrology reading generate karunga. Sabse pehle, apna naam bataiye.", 1200);
        setChatStep(1);
      }, 1400);
    }, 300);
  }, []); // eslint-disable-line

  const handleSend = () => {
    if (!inputValue.trim()) return;
    const val = inputValue.trim();
    setChatMessages(prev => [...prev, { text: val, isUser: true, isNew: true }]);
    setInputValue("");

    if (chatStep === 1) {
      setFormData(prev => ({ ...prev, name: val }));
      setTimeout(() => {
        addAIMessage(`${val}! Bahut achha naam hai ✨\n\nAb apni Date of Birth enter karein (DD/MM/YYYY).`, 800);
        setChatStep(2);
      }, 300);
    } else if (chatStep === 2) {
      const parts = val.replace(/[-.\/]/g, "/").split("/");
      let dateStr = "";
      if (parts.length === 3) {
        const [d, m, y] = parts;
        dateStr = `${y.length === 4 ? y : "19" + y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
      } else {
        dateStr = val;
      }
      setFormData(prev => ({ ...prev, birth_date: dateStr }));
      setTimeout(() => {
        addAIMessage("Perfect! 🎯\n\nAapka gender kya hai?\n\n• Male\n• Female\n• Other", 800);
        setChatStep(3);
      }, 300);
    } else if (chatStep === 3) {
      setFormData(prev => ({ ...prev, gender: val.toLowerCase() }));
      setTimeout(() => {
        addAIMessage("Ab apna question likhiye — Love, Career, Money ya Future Partner ke baare mein pooch sakte hain. 🔮", 800);
        setChatStep(4);
      }, 300);
    } else if (chatStep === 4) {
      setFormData(prev => ({ ...prev, question: val }));
      setTimeout(() => {
        addAIMessage(`Shukriya ${formData.name}! 🙏\n\nMain ab aapki reading analyze kar raha hoon...`, 800);
        setChatStep(5);
        setTimeout(() => startAnalysis({ ...formData, question: val }), 2000);
      }, 300);
    }
  };

  const startAnalysis = async (data) => {
    setPhase("analyzing");
    try {
      const sessionRes = await axios.post(`${API}/sessions`, {
        name: data.name, birth_date: data.birth_date, gender: data.gender || "not specified",
        question: data.question, language, source: testSource,
        ref_code: getActiveReferral(),
      });
      setSessionId(sessionRes.data.session_id);
    } catch {
      toast.error(language === "hi" ? "कुछ गलत हुआ" : "Something went wrong.");
    }
    for (let i = 0; i < analysisSteps.length; i++) {
      await new Promise(r => setTimeout(r, 1800 + Math.random() * 800));
      setAnalysisStep(i + 1);
    }
    setTimeout(() => setPhase("report"), 800);
  };

  // Re-open an already-paid report (e.g. after the Premium chat ends)
  useEffect(() => {
    if (!resumeSessionId) return;
    (async () => {
      try {
        const [reportRes, statusRes] = await Promise.all([
          axios.get(`${API}/sessions/${resumeSessionId}/report`),
          axios.get(`${API}/sessions/${resumeSessionId}/status`),
        ]);
        setSessionId(resumeSessionId);
        setFormData(f => ({ ...f, name: reportRes.data.name || "", birth_date: reportRes.data.birth_date || "" }));
        setReport(reportRes.data.report);
        setIsPremium(reportRes.data.is_premium);
        setChatEnded(!statusRes.data.chat_active);
        setPhase("unlocked");
      } catch { toast.error("Report not found"); navigate("/"); }
    })();
  }, [resumeSessionId]); // eslint-disable-line

  const handleUnlock = async (plan = "basic") => {
    if (!sessionId) return;
    setPayLoading(true);
    GA.paymentInitiated(plan === "premium" ? 199 : 49);
    try {
      const orderRes = await axios.post(`${API}/payments/create-order?session_id=${sessionId}&plan=${plan}`);
      const orderData = orderRes.data;
      const options = {
        key: orderData.razorpay_key_id, amount: orderData.amount, currency: orderData.currency,
        name: "Vishu Astro",
        description: plan === "premium"
          ? (language === "hi" ? "प्रीमियम रिपोर्ट + 10 मिनट चैट" : "Premium Report + 10-min Chat")
          : (language === "hi" ? "3 मिनट का ज्योतिष सत्र" : "3-minute Astrology Session"),
        order_id: orderData.order_id,
        prefill: { name: formData.name },
        handler: async (response) => {
          try {
            const verifyRes = await axios.post(`${API}/payments/verify`, {
              session_id: sessionId, razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id, razorpay_signature: response.razorpay_signature,
            });
            if (verifyRes.data.duplicate) {
              toast.info("Yeh report pehle se unlocked hai. Extra payment refund review ke liye note kar liya gaya hai.", { duration: 8000 });
              try {
                const r = await axios.get(`${API}/sessions/${sessionId}/report`);
                setReport(r.data.report); setIsPremium(r.data.is_premium);
              } catch { /* ignore */ }
              setPayLoading(false); setPhase("unlocked");
              return;
            }
            GA.paymentSuccess(plan === "premium" ? 199 : 49, response.razorpay_order_id);
            confetti({ particleCount: 150, spread: 80, origin: { y: 0.6 }, colors: ['#FFC107', '#FFB300', '#FF9800', '#FFD54F'] });
            setTimeout(() => confetti({ particleCount: 80, spread: 60, origin: { y: 0.7, x: 0.3 } }), 300);
            setTimeout(() => confetti({ particleCount: 80, spread: 60, origin: { y: 0.7, x: 0.7 } }), 500);
            toast.success(plan === "premium" ? "👑 Premium Unlocked!" : (language === "hi" ? "🎉 भुगतान सफल! रिपोर्ट अनलॉक" : "🎉 Payment Successful! Report Unlocked"));
            try {
              const reportRes = await axios.get(`${API}/sessions/${sessionId}/report`);
              setReport(reportRes.data.report);
              setIsPremium(reportRes.data.is_premium || plan === "premium");
            } catch { /* report fetch failed */ }
            setPayLoading(false);
            setPhase("unlocked");
          } catch { toast.error(language === "hi" ? "भुगतान सत्यापन विफल" : "Payment verification failed."); setPayLoading(false); }
        },
        modal: { ondismiss: () => setPayLoading(false) },
        theme: { color: "#FFC107" },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => { toast.error(language === "hi" ? "भुगतान विफल" : "Payment Failed."); setPayLoading(false); });
      rzp.open();
    } catch { toast.error(language === "hi" ? "कुछ गलत हुआ" : "Something went wrong."); setPayLoading(false); }
  };

  const handlePremiumUpgrade = async () => {
    if (!sessionId) return toast.error("Session expired.");
    setPayLoading(true);
    try {
      const orderRes = await axios.post(`${API}/sessions/${sessionId}/recharge`);
      const orderData = orderRes.data;
      const options = {
        key: orderData.razorpay_key_id, amount: orderData.amount, currency: orderData.currency,
        name: "Vishu Astro",
        description: "Upgrade to Premium — ₹150",
        order_id: orderData.order_id,
        prefill: { name: formData.name },
        handler: async (response) => {
          try {
            await axios.post(`${API}/sessions/${sessionId}/recharge-verify`, {
              session_id: sessionId, razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id, razorpay_signature: response.razorpay_signature,
            });
            toast.success("👑 Premium Unlocked! All features + 10 min total chat");
            confetti({ particleCount: 150, spread: 80, origin: { y: 0.6 }, colors: ['#FFC107', '#FFB300', '#FF9800'] });
            try {
              const reportRes = await axios.get(`${API}/sessions/${sessionId}/report`);
              setReport(reportRes.data.report);
              setIsPremium(reportRes.data.is_premium);
            } catch { /* ignore */ }
            setChatEnded(false);
          } catch { toast.error("Payment verification failed."); }
          setPayLoading(false);
        },
        modal: { ondismiss: () => setPayLoading(false) },
        theme: { color: "#FFC107" },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => { toast.error("Payment Failed."); setPayLoading(false); });
      rzp.open();
    } catch (err) {
      if (err.response?.status === 409) {
        toast.info("Already Premium");
        try { const r = await axios.get(`${API}/sessions/${sessionId}/report`); setReport(r.data.report); setIsPremium(r.data.is_premium); } catch { /* ignore */ }
      } else toast.error("Something went wrong.");
      setPayLoading(false);
    }
  };

  const downloadPDF = () => {
    if (!report) return;
    try { downloadReportPdf(report, formData); toast.success("PDF downloaded"); }
    catch { toast.error("PDF download failed. Please try again."); }
  };

  const getPlaceholder = () => {
    if (chatStep === 1) return "Apna naam type karein...";
    if (chatStep === 2) return "DD/MM/YYYY";
    if (chatStep === 3) return "Male / Female / Other";
    if (chatStep === 4) return "Apna sawaal type karein...";
    return "";
  };

  return (
    <div className="min-h-screen bg-white" data-testid="curiosity-page">
      <AnimatePresence mode="wait">
        {/* ===== CHAT PHASE ===== */}
        {phase === "chat" && (
          <motion.div key="chat" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="min-h-screen flex flex-col">
            <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-xl border-b border-gray-100 px-4 py-3">
              <div className="flex items-center gap-3 max-w-lg mx-auto">
                <button onClick={() => navigate("/")} className="p-1 text-gray-500 hover:text-gray-900 transition-colors"><ChevronLeft className="w-6 h-6" /></button>
                <img src="/logo.jpg" alt="Vishu Astro" className="w-10 h-10 rounded-full object-cover shadow-sm" />
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 text-sm" style={{ fontFamily: 'Space Grotesk' }}>Vishu Astro</h3>
                  <p className="text-xs text-green-500 font-medium flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" /> {language === "hi" ? "ऑनलाइन" : "Online"}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4 max-w-lg mx-auto w-full">
              {chatMessages.map((msg, i) => (
                <ChatBubble key={i} message={msg.text} isUser={msg.isUser} isNew={msg.isNew} />
              ))}
              {typing && (
                <div className="flex items-start gap-2 mb-3">
                  <img src="/logo.jpg" alt="" className="w-8 h-8 rounded-full object-cover mt-1 shadow-sm" />
                  <div className="chat-bubble-ai rounded-2xl rounded-bl-md"><TypingIndicator /></div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            {chatStep >= 1 && chatStep <= 4 && (
              <div className="sticky bottom-0 bg-white/95 backdrop-blur-xl border-t border-gray-100 px-4 py-3">
                <div className="flex items-center gap-2 max-w-lg mx-auto">
                  <input ref={inputRef} value={inputValue} onChange={e => setInputValue(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleSend()} placeholder={getPlaceholder()}
                    type="text"
                    className="flex-1 bg-gray-50 border border-gray-200 rounded-full px-5 py-3 text-sm focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all"
                    data-testid="chat-input" autoFocus />
                  <button onClick={handleSend}
                    className="w-11 h-11 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all"
                    data-testid="chat-send-btn">
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* ===== ANALYSIS PHASE ===== */}
        {phase === "analyzing" && (
          <motion.div key="analyzing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-b from-amber-50/50 to-white">
            <div className="w-full max-w-md text-center">
              <motion.div initial={{ scale: 0.8 }} animate={{ scale: 1 }} className="mb-8">
                <div className="relative inline-block">
                  <div className="absolute inset-0 w-24 h-24 rounded-full bg-amber-300 blur-xl opacity-40 animate-pulse-glow mx-auto" style={{ left: '50%', transform: 'translateX(-50%)' }} />
                  <img src="/logo.jpg" alt="" className="w-24 h-24 rounded-full object-cover shadow-xl animate-float mx-auto relative z-10 border-4 border-white" />
                </div>
              </motion.div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk' }}>
                {language === "hi" ? "रिपोर्ट बन रही है..." : "Generating Your Report..."}
              </h2>
              <p className="text-gray-500 text-sm mb-10">
                {language === "hi" ? `${formData.name}, कृपया रुकें... AI विश्लेषण जारी है` : `${formData.name}, please wait... AI analysis in progress`}
              </p>
              <div className="space-y-5 text-left">
                {analysisSteps.map((step, i) => (
                  <motion.div key={i} initial={{ opacity: 0, x: -20 }} animate={{ opacity: i < analysisStep ? 1 : 0.3, x: 0 }}
                    transition={{ delay: i * 0.1, duration: 0.4 }} className="flex items-center gap-3">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold transition-all duration-500 ${
                      i < analysisStep ? "bg-green-100 text-green-600" : i === analysisStep ? "bg-amber-100 text-amber-600 animate-pulse" : "bg-gray-100 text-gray-400"
                    }`}>
                      {i < analysisStep ? <Check className="w-4 h-4" /> : i + 1}
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${i < analysisStep ? "text-gray-900" : "text-gray-400"}`}>
                        {language === "hi" ? step.hi : step.en}
                      </p>
                      {i === analysisStep && (
                        <div className="analysis-bar mt-1.5">
                          <div className="analysis-bar-fill" style={{ animationDuration: '1.5s' }} />
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
              <div className="mt-10 text-xs text-gray-400">
                {Math.min(Math.round((analysisStep / analysisSteps.length) * 100), 100)}% {language === "hi" ? "पूर्ण" : "complete"}
              </div>
            </div>
          </motion.div>
        )}

        {/* ===== REPORT PHASE (Blurred + Payment) ===== */}
        {phase === "report" && (
          <motion.div key="report" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="min-h-screen px-4 py-8 bg-gradient-to-b from-amber-50/30 to-white">
            <div className="max-w-lg mx-auto">
              <div className="text-center mb-8 pt-4">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200 }}
                  className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-600" />
                </motion.div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk' }}>
                  {language === "hi" ? "रिपोर्ट तैयार है!" : "Your Report is Ready!"}
                </h2>
                <p className="text-gray-500 text-sm">
                  {language === "hi" ? `${formData.name}, पूरी रिपोर्ट अनलॉक करें` : `${formData.name}, unlock your full report below`}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-8">
                {reportCards.map((c, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="glass-card rounded-2xl p-5 text-center relative overflow-hidden">
                    <div className="text-2xl mb-1">{c.emoji}</div>
                    <p className="text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wider">{language === "hi" ? c.hi : c.en}</p>
                    <div className="blur-card-content">
                      <p className="text-xl font-bold" style={{ color: c.color }}>{c.score}</p>
                    </div>
                    <div className="absolute top-2 right-2">
                      <Lock className="w-3.5 h-3.5 text-gray-300" />
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* ===== TWO-TIER PRICING (Basic ₹49 / Premium ₹199) ===== */}
              {/* BASIC CARD */}
              <motion.div initial={{ y: 20 }} animate={{ y: 0 }}
                className="bg-white rounded-3xl p-6 mb-5 border border-gray-200 shadow-lg" data-testid="basic-plan-card">
                <div className="text-center">
                  <span className="inline-block bg-gray-100 text-gray-600 text-xs font-bold uppercase tracking-widest px-4 py-1 rounded-full mb-3">Basic</span>
                  <div className="inline-flex items-center gap-2 bg-red-50 text-red-500 text-xs font-bold px-3 py-1 rounded-full mb-3 ml-2">🔥 81% OFF</div>
                  <div className="flex items-center justify-center gap-3 mb-1">
                    <span className="text-gray-400 line-through text-xl">₹199</span>
                    <span className="text-4xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>₹49</span>
                  </div>
                  <p className="text-gray-400 text-xs mb-5">One-time payment • No subscription</p>
                </div>
                <div className="space-y-2.5 mb-5 text-left">
                  {[
                    { emoji: "💍", text: "Love / Arrange Marriage" },
                    { emoji: "💼", text: "Career & Money Flow" },
                    { emoji: "🌙", text: "Aura & Personality Trait" },
                    { emoji: "⭐", text: "Hidden Talent" },
                    { emoji: "🎯", text: "Life Path Energy" },
                    { emoji: "✨", text: "Lucky Colour & Day" },
                    { emoji: "💖", text: "Love Compatibility" },
                    { emoji: "💬", text: "3-Minute Chat" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm text-gray-700">
                      <span className="text-base">{item.emoji}</span>
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>
                <Button onClick={() => handleUnlock("basic")} disabled={payLoading}
                  className="w-full py-4 text-base bg-gradient-to-r from-amber-400 to-amber-500 text-gray-900 font-bold rounded-2xl shadow-md hover:shadow-lg transition-all"
                  data-testid="unlock-basic-btn">
                  {payLoading ? (
                    <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-gray-900/30 border-t-gray-900 rounded-full animate-spin" />Processing...</span>
                  ) : (
                    <span className="flex items-center gap-2">🔓 Unlock — ₹49</span>
                  )}
                </Button>
                <p className="text-xs text-gray-400 mt-3 text-center flex items-center justify-center gap-1">
                  <Lock className="w-3 h-3" /> 100% Secure Payment
                </p>
              </motion.div>

              {/* PREMIUM CARD */}
              <motion.div initial={{ y: 20 }} animate={{ y: 0 }} transition={{ delay: 0.1 }}
                className="rounded-3xl p-6 mb-6 relative overflow-hidden"
                style={{ background: 'linear-gradient(160deg, #1a1207 0%, #2d1f0a 50%, #1a1207 100%)', border: '2px solid #FFC107' }}
                data-testid="premium-plan-card">
                <div className="absolute top-4 right-4 bg-gradient-to-r from-amber-400 to-amber-500 text-gray-900 text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">👑 Premium</div>
                <div className="text-center">
                  <span className="inline-block bg-amber-500/20 text-amber-400 text-xs font-bold uppercase tracking-widest px-4 py-1 rounded-full mb-3 border border-amber-500/30">✨ Best Value</span>
                  <div className="flex items-center justify-center gap-3 mb-1">
                    <span className="text-gray-500 line-through text-xl">₹399</span>
                    <span className="text-4xl font-bold text-amber-400" style={{ fontFamily: 'Space Grotesk' }}>₹199</span>
                  </div>
                  <p className="text-amber-200/60 text-xs mb-5">Everything in Basic + deep, detailed analysis</p>
                </div>
                <div className="space-y-2.5 mb-5 text-left">
                  {[
                    { emoji: "✅", text: "Everything in Basic" },
                    { emoji: "💖", text: "Relationship & Compat." },
                    { emoji: "💰", text: "Money Secret & Blockages" },
                    { emoji: "🧠", text: "Deep Weakness + Strength" },
                    { emoji: "🔢", text: "Deep Numerology Breakdown" },
                    { emoji: "📄", text: "Downloadable PDF Report" },
                    { emoji: "🔮", text: "5-Year Future Timeline" },
                    { emoji: "💬", text: "10-Minute Chat" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 text-sm text-amber-100/90">
                      <span className="text-base">{item.emoji}</span>
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>
                <Button onClick={() => handleUnlock("premium")} disabled={payLoading}
                  className="w-full py-4 text-base bg-gradient-to-r from-amber-400 to-amber-600 text-gray-900 font-bold rounded-2xl shadow-lg shadow-amber-900/30 hover:shadow-xl transition-all"
                  data-testid="unlock-premium-btn">
                  {payLoading ? (
                    <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-gray-900/30 border-t-gray-900 rounded-full animate-spin" />Processing...</span>
                  ) : (
                    <span className="flex items-center gap-2">👑 Unlock Premium — ₹199</span>
                  )}
                </Button>
                <p className="text-xs text-amber-200/40 mt-3 text-center flex items-center justify-center gap-1">
                  <Lock className="w-3 h-3" /> VIP • 100% Secure
                </p>
              </motion.div>

              <div className="mb-5"><CountdownTimer /></div>
              <div className="md:hidden h-36" />
              <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 px-4 pt-3 bg-white/95 backdrop-blur-xl border-t border-gray-100 space-y-2" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
                <Button onClick={() => handleUnlock("basic")} disabled={payLoading} className="btn-primary w-full py-3.5 text-base" data-testid="mobile-sticky-unlock">
                  {payLoading ? (
                    <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-gray-900/30 border-t-gray-900 rounded-full animate-spin" />...</span>
                  ) : (
                    <span className="flex items-center gap-2"><Lock className="w-4 h-4" /> Unlock — ₹49</span>
                  )}
                </Button>
                <Button onClick={() => handleUnlock("premium")} disabled={payLoading}
                  className="w-full py-3.5 text-base font-bold rounded-2xl text-amber-400 border-2 border-amber-400 hover:opacity-90"
                  style={{ background: 'linear-gradient(160deg, #1a1207 0%, #2d1f0a 100%)' }} data-testid="mobile-sticky-unlock-premium">
                  {payLoading ? "..." : <span className="flex items-center gap-2">👑 Unlock Premium — ₹199</span>}
                </Button>
              </div>
            </div>
            {/* Social Proof Toast on payment page */}
            <SocialProofToast language={language} />
          </motion.div>
        )}

        {/* ===== UNLOCKED REPORT PHASE ===== */}
        {phase === "unlocked" && (
          <motion.div key="unlocked" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="min-h-screen px-4 py-8 bg-gradient-to-b from-amber-50/30 to-white" data-testid="unlocked-report">
            <div className="max-w-lg mx-auto">
              {/* Success Header */}
              <div className="text-center mb-8 pt-4">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200 }}
                  className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Sparkles className="w-10 h-10 text-white" />
                </motion.div>
                <h2 className="text-2xl font-bold text-gray-900 mb-1" style={{ fontFamily: 'Space Grotesk' }}>
                  {language === "hi" ? `${formData.name}, आपकी रिपोर्ट!` : `${formData.name}'s Full Report`}
                </h2>
                <p className="text-gray-500 text-sm">{language === "hi" ? "AI ज्योतिष विश्लेषण पूरा" : "AI Astrology Analysis Complete"}</p>
                {report?.zodiac && (
                  <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-50 rounded-full mt-3 text-sm font-semibold text-amber-700">
                    <Star className="w-4 h-4" /> {report.zodiac}
                  </div>
                )}
              </div>

              {report ? (
                <>
                  {/* Score Cards with Progress Bars */}
                  <div className="space-y-3 mb-8">
                    <ScoreBar label={language === "hi" ? "प्यार का स्कोर" : "Love Score"} score={report.love_score} color="#FF4D6A" delay={100}
                      icon={<Heart className="w-4 h-4" style={{ color: '#FF4D6A' }} />} />
                    <ScoreBar label={language === "hi" ? "करियर स्कोर" : "Career Score"} score={report.career_score} color="#4CAF50" delay={200}
                      icon={<Briefcase className="w-4 h-4" style={{ color: '#4CAF50' }} />} />
                    <ScoreBar label={language === "hi" ? "धन स्कोर" : "Wealth Score"} score={report.wealth_score} color="#FFC107" delay={300}
                      icon={<DollarSign className="w-4 h-4" style={{ color: '#FFC107' }} />} />
                    <ScoreBar label={language === "hi" ? "विवाह स्कोर" : "Marriage Score"} score={report.marriage_score} color="#E91E63" delay={400}
                      icon={<Gem className="w-4 h-4" style={{ color: '#E91E63' }} />} />
                    <ScoreBar label={language === "hi" ? "स्वास्थ्य स्कोर" : "Health Score"} score={report.health_score} color="#00BCD4" delay={500}
                      icon={<Activity className="w-4 h-4" style={{ color: '#00BCD4' }} />} />
                    <ScoreBar label={language === "hi" ? "व्यक्तित्व स्कोर" : "Personality Score"} score={report.personality_score} color="#2196F3" delay={600}
                      icon={<Brain className="w-4 h-4" style={{ color: '#2196F3' }} />} />
                  </div>

                  {/* Info Cards */}
                  <div className="grid grid-cols-3 gap-3 mb-6">
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
                      className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm text-center">
                      <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center mx-auto mb-2">
                        <Star className="w-5 h-5 text-purple-500" />
                      </div>
                      <p className="text-xs text-gray-500 mb-1">Lucky Number</p>
                      <p className="text-sm font-bold text-gray-900">{report.lucky_numbers?.join(", ")}</p>
                    </motion.div>
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}
                      className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm text-center">
                      <div className="w-10 h-10 rounded-xl bg-pink-50 flex items-center justify-center mx-auto mb-2">
                        <Palette className="w-5 h-5 text-pink-500" />
                      </div>
                      <p className="text-xs text-gray-500 mb-1">Lucky Color</p>
                      <p className="text-sm font-bold text-gray-900">{report.lucky_color}</p>
                    </motion.div>
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}
                      className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm text-center">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center mx-auto mb-2">
                        <Calendar className="w-5 h-5 text-blue-500" />
                      </div>
                      <p className="text-xs text-gray-500 mb-1">Lucky Day</p>
                      <p className="text-sm font-bold text-gray-900">{report.lucky_day}</p>
                    </motion.div>
                  </div>

                  {/* Strengths & Weaknesses */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.0 }}
                      className="bg-white rounded-2xl p-4 border border-green-100 shadow-sm">
                      <div className="flex items-center gap-2 mb-3">
                        <Shield className="w-4 h-4 text-green-500" />
                        <span className="text-sm font-bold text-gray-900">Strengths</span>
                      </div>
                      <div className="space-y-2">
                        {report.strengths?.map((s, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs text-gray-700">
                            <div className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" />
                            <span>{s}</span>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.1 }}
                      className="bg-white rounded-2xl p-4 border border-red-100 shadow-sm">
                      <div className="flex items-center gap-2 mb-3">
                        <Zap className="w-4 h-4 text-red-400" />
                        <span className="text-sm font-bold text-gray-900">Weaknesses</span>
                      </div>
                      <div className="space-y-2">
                        {report.weaknesses?.map((w, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs text-gray-700">
                            <div className="w-1.5 h-1.5 rounded-full bg-red-300 flex-shrink-0" />
                            <span>{w}</span>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  </div>

                  {/* Hidden Talent & Life Path */}
                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2 }}
                    className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl p-5 border border-purple-100 mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Eye className="w-5 h-5 text-purple-500" />
                      <span className="text-sm font-bold text-gray-900">Hidden Talent</span>
                    </div>
                    <p className="text-sm text-gray-700">{report.hidden_talent}</p>
                  </motion.div>

                  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3 }}
                    className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-5 border border-amber-100 mb-8">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="w-5 h-5 text-amber-600" />
                      <span className="text-sm font-bold text-gray-900">Life Path</span>
                    </div>
                    <p className="text-sm text-gray-700">{report.life_path}</p>
                  </motion.div>

                  {/* ===== PREMIUM SECTIONS ===== */}
                  {isPremium && report.money_secret ? (
                    <div className="space-y-4 mb-8">
                      <div className="text-center mb-2">
                        <span className="inline-block bg-gradient-to-r from-amber-400 to-amber-600 text-gray-900 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full">👑 Premium Content</span>
                      </div>

                      {/* Personality & Traits (Deep) */}
                      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.35 }}
                        className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-lg">🔮</span>
                          <span className="text-sm font-bold text-gray-900 uppercase tracking-wide" style={{ color: '#7B1FA2' }}>Personality & Traits</span>
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm text-gray-700"><span className="font-semibold text-green-700">Deep Strength:</span> {report.deep_strength}</p>
                          <p className="text-sm text-gray-700"><span className="font-semibold text-red-500">Deep Weakness:</span> {report.deep_weakness}</p>
                        </div>
                      </motion.div>

                      {/* Love & Compatibility (Deep) */}
                      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.4 }}
                        className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-lg">💕</span>
                          <span className="text-sm font-bold uppercase tracking-wide" style={{ color: '#C2185B' }}>Love & Compatibility</span>
                        </div>
                        <p className="text-sm text-gray-700">{report.relationship_deep}</p>
                      </motion.div>

                      {/* Career & Money (Deep) + Money Secret */}
                      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.45 }}
                        className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-lg">💰</span>
                          <span className="text-sm font-bold uppercase tracking-wide" style={{ color: '#B8860B' }}>Money Secret & Blockages</span>
                        </div>
                        <p className="text-sm text-gray-700 mb-2">{report.money_secret}</p>
                        <p className="text-sm text-gray-700"><span className="font-semibold text-red-500">Blockage:</span> {report.money_blockage}</p>
                      </motion.div>

                      {/* Aura & Energy */}
                      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5 }}
                        className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-lg">🌙</span>
                          <span className="text-sm font-bold uppercase tracking-wide" style={{ color: '#4527A0' }}>Aura & Energy</span>
                        </div>
                        <p className="text-sm text-gray-700">{report.aura_energy}</p>
                      </motion.div>

                      {/* Deep Numerology Breakdown */}
                      {report.numerology && (
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.55 }}
                          className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-lg">🔢</span>
                            <span className="text-sm font-bold uppercase tracking-wide" style={{ color: '#1565C0' }}>Deep Numerology Breakdown</span>
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-center">
                            <div className="bg-blue-50 rounded-xl p-3">
                              <p className="text-xl font-bold text-blue-700" style={{ fontFamily: 'Space Grotesk' }}>{report.numerology.life_path_number}</p>
                              <p className="text-[10px] text-blue-500 font-semibold uppercase">Life Path</p>
                            </div>
                            <div className="bg-purple-50 rounded-xl p-3">
                              <p className="text-xl font-bold text-purple-700" style={{ fontFamily: 'Space Grotesk' }}>{report.numerology.birth_day_number}</p>
                              <p className="text-[10px] text-purple-500 font-semibold uppercase">Birth Day</p>
                            </div>
                            <div className="bg-teal-50 rounded-xl p-3">
                              <p className="text-xl font-bold text-teal-700" style={{ fontFamily: 'Space Grotesk' }}>{report.numerology.destiny_number}</p>
                              <p className="text-[10px] text-teal-500 font-semibold uppercase">Destiny</p>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500 mt-2">{report.numerology.life_path_meaning}</p>
                        </motion.div>
                      )}

                      {/* 5-Year Future Timeline */}
                      {report.five_year_timeline && (
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.6 }}
                          className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-lg">🔮</span>
                            <span className="text-sm font-bold uppercase tracking-wide" style={{ color: '#6A1B9A' }}>5-Year Future Timeline</span>
                          </div>
                          <div className="space-y-2">
                            {report.five_year_timeline.map((yr, i) => (
                              <div key={i} className="flex items-center gap-3">
                                <span className="text-xs font-bold text-amber-700 bg-amber-50 px-2.5 py-1 rounded-lg w-14 text-center" style={{ fontFamily: 'Space Grotesk' }}>{yr.year}</span>
                                <span className="text-sm text-gray-700">{yr.theme}</span>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}

                      {/* PDF Download */}
                      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.65 }} className="text-center">
                        <Button variant="outline" onClick={() => downloadPDF()} className="rounded-2xl border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="download-pdf-btn">
                          📄 Download PDF Report
                        </Button>
                      </motion.div>
                    </div>
                  ) : (
                    /* ===== LOCKED PREMIUM PREVIEW (Basic users) ===== */
                    <div className="space-y-4 mb-8">
                      <div className="text-center mb-2">
                        <span className="inline-block bg-gray-100 text-gray-500 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full">👑 Premium Content — Locked</span>
                      </div>
                      {[
                        { emoji: "🔮", title: "Personality & Traits", color: "#7B1FA2" },
                        { emoji: "💕", title: "Love & Compatibility", color: "#C2185B" },
                        { emoji: "💰", title: "Money Secret & Blockages", color: "#B8860B" },
                        { emoji: "🌙", title: "Aura & Energy", color: "#4527A0" },
                        { emoji: "🔢", title: "Deep Numerology Breakdown", color: "#1565C0" },
                        { emoji: "🔮", title: "5-Year Future Timeline", color: "#6A1B9A" },
                        { emoji: "📄", title: "Downloadable PDF Report", color: "#37474F" },
                      ].map((s, i) => (
                        <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.35 + i * 0.05 }}
                          className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm relative overflow-hidden">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">{s.emoji}</span>
                            <span className="text-sm font-bold uppercase tracking-wide" style={{ color: s.color }}>{s.title}</span>
                          </div>
                          <div className="space-y-2">
                            <div className="h-3 bg-gray-200 rounded-full w-full blur-[2px] opacity-60" />
                            <div className="h-3 bg-gray-200 rounded-full w-4/5 blur-[2px] opacity-60" />
                            <div className="h-3 bg-gray-200 rounded-full w-3/5 blur-[2px] opacity-60" />
                          </div>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Lock className="w-6 h-6 text-gray-400" />
                          </div>
                        </motion.div>
                      ))}

                      {/* Upgrade CTA */}
                      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.8 }}
                        className="rounded-2xl p-5 text-center relative overflow-hidden"
                        style={{ background: 'linear-gradient(160deg, #1a1207 0%, #2d1f0a 50%, #1a1207 100%)', border: '2px solid #FFC107' }}>
                        <p className="text-amber-400 font-bold text-sm mb-1">👑 Unlock All Premium Features</p>
                        <p className="text-amber-200/60 text-xs mb-3">Deep analysis + Numerology + 5-Year Timeline + PDF + 10 min total chat</p>
                        <div className="flex items-center justify-center gap-2 mb-3">
                          <span className="text-gray-500 line-through text-sm">₹199</span>
                          <span className="text-2xl font-bold text-amber-400" style={{ fontFamily: 'Space Grotesk' }}>₹150</span>
                          <span className="text-amber-200/50 text-[10px]">(you already paid ₹49)</span>
                        </div>
                        <Button onClick={handlePremiumUpgrade} disabled={payLoading}
                          className="w-full py-3.5 bg-gradient-to-r from-amber-400 to-amber-600 text-gray-900 font-bold rounded-2xl"
                          data-testid="upgrade-premium-btn">
                          {payLoading ? "Processing..." : "👑 Upgrade to Premium — ₹150"}
                        </Button>
                      </motion.div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12 text-gray-400 text-sm">Loading report...</div>
              )}

              {/* CTA to Chat */}
              {!chatEnded && <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5 }}
                className="glass-card rounded-3xl p-6 text-center border-2 border-amber-300">
                <h3 className="text-lg font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk' }}>
                  {language === "hi" ? "अब अपने ज्योतिषी से बात करें!" : "Now Chat with Your Astrologer!"}
                </h3>
                <p className="text-gray-500 text-sm mb-4">
                  {isPremium ? "Your 10-minute live session is active — ask questions now" : "Your 3-minute live session is active — ask questions now"}
                </p>
                <Button onClick={() => navigate(`/chat/${sessionId}`)}
                  className="btn-primary w-full py-4 text-base" data-testid="start-chat-btn">
                  <span className="flex items-center gap-2"><ArrowRight className="w-4 h-4" /> {language === "hi" ? "चैट शुरू करें" : "Start Chat"}</span>
                </Button>
              </motion.div>}
              <div className="h-8" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
